import React from 'react'
import { BrandAppearance, RefZone } from '../../../services/broadcastAdminService'
import { valueOf, formatMoney, formatAreaSeconds } from '../broadcastValuation'
import LocationSplitBar from './LocationSplitBar'

interface Props {
  rows: BrandAppearance[]
  zones: RefZone[]
  /** The broadcast's 30-second rate; null when it is not priced yet. */
  rate: number | null
  currency: string
}

/**
 * One brand's sponsor value split by location in venue, as a single line.
 *
 * Each location's area-seconds priced at the broadcast's own 30-second rate —
 * valueOf, the same call behind the Sponsor value card, so the segments are
 * that card broken down by where on screen it was earned. An unpriced
 * broadcast falls back to area-seconds and says so in the title, as the
 * library donuts do, rather than drawing area under a money label.
 *
 * Moving an appearance to another location moves its value with it, live.
 *
 * Excluded appearances are left out: they count toward nothing else on the
 * page either.
 */
const AppearanceLocationBar: React.FC<Props> = ({ rows, zones, rate, currency }) => {
  // Truthiness, not `=== null`: a CRO older than the one that sends
  // excluded_reason leaves the field undefined, which is not an exclusion.
  const counted = rows.filter((r) => !r.excluded_reason)
  if (counted.length === 0) return null

  const priced = rate !== null
  // Priced per location after summing area, not per appearance: the rate is
  // linear, so the result is identical and there is one multiplication.
  const area = new Map<string, number>()
  for (const r of counted) area.set(r.zone_code, (area.get(r.zone_code) ?? 0) + Number(r.area_seconds ?? 0))
  const amounts = new Map<string, number>()
  for (const [code, a] of area) amounts.set(code, priced ? (valueOf(a, rate).value ?? 0) : a)

  return (
    <LocationSplitBar
      title={priced ? 'Sponsor value by placement' : 'Area-seconds by placement'}
      amounts={amounts}
      labelOf={(code) => zones.find((z) => z.code === code)?.label ?? code}
      format={(n) => priced ? formatMoney(n, currency) : formatAreaSeconds(n)}
    />
  )
}

export default AppearanceLocationBar
